import {
  type ProductFeedbackDiagnosticEvent,
  type ProductFeedbackExportModel,
  type ProductFeedbackMarkdownExport,
} from '../types/productFeedback';
import { normalizeProductFeedbackTimestamp } from './productFeedbackTimestamp';

const UNAVAILABLE = 'Unavailable.';
const UTF8_BOM = new Uint8Array([0xef, 0xbb, 0xbf]);

const CATEGORY_LABELS: Record<ProductFeedbackExportModel['report']['category'], string> = {
  bug: 'Bug',
  ux_problem: 'UX problem',
  visual_issue: 'Visual issue',
  suggestion: 'Suggestion',
};

const STATUS_LABELS: Record<ProductFeedbackExportModel['report']['status'], string> = {
  open: 'Open',
  investigating: 'Investigating',
  resolved: 'Resolved',
};

const normalizeLines = (value: string): string => value.replace(/\r\n?/g, '\n');

const formatIso = (value: unknown): string =>
  normalizeProductFeedbackTimestamp(value) || UNAVAILABLE;

const formatUtc = (value: unknown, includeMilliseconds = true): string => {
  const iso = formatIso(value);
  if (iso === UNAVAILABLE) {
    return iso;
  }

  const withoutZone = iso.slice(0, includeMilliseconds ? -1 : -5);
  return `${withoutZone.replace('T', ' ')} UTC`;
};

const escapeInlineMarkdown = (value: string): string =>
  normalizeLines(value)
    .replace(/\s*\n\s*/g, ' ')
    .replace(/([\\`*_[\]{}<>|])/g, '\\$1');

const inlineCode = (value: string): string => {
  const normalized = normalizeLines(value).replace(/\s*\n\s*/g, ' ');
  const backtickRuns: string[] = normalized.match(/`+/g) || [];
  const longestRun = backtickRuns.reduce((max, run) => Math.max(max, run.length), 0);
  const fence = '`'.repeat(Math.max(1, longestRun + 1));
  return `${fence}${normalized}${fence}`;
};

const hasText = (value: string | null): value is string => Boolean(value && value.trim());

const fencedText = (value: string): string => {
  const normalized = normalizeLines(value);
  const backtickRuns: string[] = normalized.match(/`+/g) || [];
  const longestRun = backtickRuns.reduce((max, run) => Math.max(max, run.length), 0);
  const fence = '`'.repeat(Math.max(3, longestRun + 1));
  return `${fence}text\n${normalized}\n${fence}`;
};

const yesNo = (value: boolean): string => (value ? 'yes' : 'no');

const formatDimensions = (width: number, height: number): string => `${width} × ${height}`;

const formatRoundedDimensions = (width: number, height: number): string =>
  formatDimensions(Math.round(width), Math.round(height));

const formatDuration = (windowMs: number): string => {
  if (windowMs % 60000 === 0) {
    const minutes = windowMs / 60000;
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  if (windowMs % 1000 === 0) {
    return `${windowMs / 1000} seconds`;
  }

  return `${windowMs} ms`;
};

const diagnosticSummary = (model: ProductFeedbackExportModel): string => {
  const { diagnostics } = model;
  if (!diagnostics.included) {
    return diagnostics.events.length
      ? `not included; canonical payload contains ${diagnostics.events.length} events`
      : 'not included';
  }

  const window = formatDuration(diagnostics.windowMs);
  if (!diagnostics.events.length) {
    return `included; no events captured in the previous ${window}`;
  }

  return `included; ${diagnostics.events.length} ${
    diagnostics.events.length === 1 ? 'event' : 'events'
  } captured in the previous ${window}`;
};

const screenshotSummary = (model: ProductFeedbackExportModel): string => {
  const { report } = model;
  if (report.attachment) {
    return report.attachmentRequested ? 'attached' : 'attached; request flag is false';
  }

  return report.attachmentRequested ? 'requested but not canonically confirmed' : 'none';
};

const renderDiagnosticEvent = (event: ProductFeedbackDiagnosticEvent, index: number): string => {
  const lines = [
    `### Event ${index + 1}`,
    '',
    `- Primary source: ${escapeInlineMarkdown(event.source)}`,
    `- Sources: ${event.sources.map(escapeInlineMarkdown).join(', ')}`,
    `- Level: ${escapeInlineMarkdown(event.level)}`,
    `- Occurred at: ${formatUtc(event.occurredAtClient)}`,
    `- Route: ${inlineCode(event.routePath)}`,
    `- Occurrences: ${event.occurrences}`,
    '',
    '**Message**',
    '',
    fencedText(event.message),
  ];

  if (event.details.length) {
    lines.push(
      '',
      '**Details**',
      '',
      ...event.details.flatMap((detail, detailIndex) => [
        `${detailIndex + 1}.`,
        '',
        fencedText(detail),
      ])
    );
  }

  if (hasText(event.stack)) {
    lines.push('', '**Stack**', '', fencedText(event.stack));
  }

  return lines.join('\n');
};

const renderRuntime = (model: ProductFeedbackExportModel): string => {
  const { runtime } = model.report;
  const lines = [
    '## Runtime',
    '',
    `- Captured at client: ${formatUtc(runtime.capturedAtClient)}`,
    `- Route: ${inlineCode(runtime.routePath)}`,
    `- Viewport: ${formatDimensions(runtime.viewport.width, runtime.viewport.height)}`,
  ];

  if (runtime.viewport.visualWidth != null && runtime.viewport.visualHeight != null) {
    lines.push(
      `- Visual viewport: ${formatRoundedDimensions(
        runtime.viewport.visualWidth,
        runtime.viewport.visualHeight
      )}`
    );
  }

  lines.push(`- DPR: ${runtime.viewport.devicePixelRatio}`);
  if (runtime.platform) {
    lines.push(`- Platform: ${escapeInlineMarkdown(runtime.platform)}`);
  }
  if (runtime.userAgent) {
    lines.push(`- User agent: ${escapeInlineMarkdown(runtime.userAgent)}`);
  }
  lines.push(
    `- App/package version: ${escapeInlineMarkdown(runtime.appVersion)}`,
    `- Online: ${yesNo(runtime.online)}`
  );

  return lines.join('\n');
};

const renderDiagnostics = (model: ProductFeedbackExportModel): string => {
  const { diagnostics } = model;
  const lines = [
    '## Diagnostics',
    '',
    `${diagnosticSummary(model)[0].toUpperCase()}${diagnosticSummary(model).slice(1)}.`,
  ];

  if (diagnostics.events.length) {
    lines.push('', diagnostics.events.map(renderDiagnosticEvent).join('\n\n'));
  }

  const integrityLines = [
    `- Serialized event bytes: ${diagnostics.bytes}`,
    ...(diagnostics.droppedEventCount
      ? [`- Dropped events: ${diagnostics.droppedEventCount}`]
      : []),
    ...(diagnostics.truncated ? ['- Truncated: yes'] : []),
  ];
  if (diagnostics.events.length || diagnostics.droppedEventCount || diagnostics.truncated) {
    lines.push('', '### Integrity', '', ...integrityLines);
  }

  return lines.join('\n');
};

const renderAttachment = (model: ProductFeedbackExportModel): string => {
  const { report } = model;
  const { attachment } = report;
  if (!attachment) {
    return [
      '## Attachment',
      '',
      report.attachmentRequested
        ? 'Screenshot requested but no canonical attachment is confirmed.'
        : 'No attachment requested.',
    ].join('\n');
  }

  const lines = [
    '## Attachment',
    '',
    report.attachmentRequested
      ? 'Canonical screenshot attached.'
      : 'Canonical screenshot attached; the attachment request flag is false.',
    '',
    `- Storage path: ${escapeInlineMarkdown(attachment.storagePath)}`,
    `- Content type: ${attachment.contentType}`,
    `- Bytes: ${attachment.bytes}`,
    `- Generation: ${escapeInlineMarkdown(attachment.generation)}`,
  ];
  if (attachment.md5Hash) {
    lines.push(`- MD5 hash: ${escapeInlineMarkdown(attachment.md5Hash)}`);
  }
  lines.push(`- Attached at: ${formatUtc(attachment.attachedAt)}`);
  return lines.join('\n');
};

const renderAccessContext = (model: ProductFeedbackExportModel): string => {
  const { reporterContext } = model.report;
  const role = escapeInlineMarkdown(reporterContext.roleSnapshot);
  const lines = [
    '## Access context',
    '',
    `- Reporter role snapshot: ${role}`,
    `- Reporter status snapshot: ${escapeInlineMarkdown(reporterContext.statusSnapshot)}`,
  ];

  if (
    reporterContext.roleSnapshot === 'admin' ||
    reporterContext.adminClaimSnapshot ||
    reporterContext.adminAuthorizationSnapshot
  ) {
    lines.push(
      `- Admin claim snapshot: ${yesNo(reporterContext.adminClaimSnapshot)}`,
      `- Admin authorization snapshot: ${yesNo(reporterContext.adminAuthorizationSnapshot)}`
    );
  } else {
    lines.push(
      `- Admin-only snapshots: not applicable to ${role} reporter (claim: no; authorization: no)`
    );
  }

  return lines.join('\n');
};

const renderSanitization = (model: ProductFeedbackExportModel): string => {
  const { diagnostics } = model;
  const hasSanitizationChanges =
    diagnostics.clientRedactionCount > 0 ||
    diagnostics.serverRedactionCount > 0 ||
    diagnostics.omittedObjectCount > 0 ||
    diagnostics.droppedEventCount > 0 ||
    diagnostics.truncated;

  if (!hasSanitizationChanges) {
    return [
      '## Sanitization',
      '',
      `Sanitizer version ${escapeInlineMarkdown(
        diagnostics.sanitizerVersion
      )}; no redactions, omissions, dropped events, or truncation recorded.`,
    ].join('\n');
  }

  return [
    '## Sanitization',
    '',
    `- Sanitizer version: ${escapeInlineMarkdown(diagnostics.sanitizerVersion)}`,
    `- Client redactions: ${diagnostics.clientRedactionCount}`,
    `- Server redactions: ${diagnostics.serverRedactionCount}`,
    `- Omitted objects: ${diagnostics.omittedObjectCount}`,
    `- Dropped events: ${diagnostics.droppedEventCount}`,
    `- Truncated: ${yesNo(diagnostics.truncated)}`,
  ].join('\n');
};

const renderResolution = (model: ProductFeedbackExportModel): string => {
  const { report } = model;
  const lines = ['## Resolution', ''];

  if (report.status !== 'resolved') {
    lines.push('Not resolved.');
  } else if (report.resolvedAt) {
    lines.push(`- Resolved at: ${formatUtc(report.resolvedAt)}`);
  } else {
    lines.push('Status is resolved, but the canonical resolution timestamp is unavailable.');
  }

  if (hasText(report.resolutionNote)) {
    lines.push('', '**Resolution note**', '', fencedText(report.resolutionNote));
  }

  return lines.join('\n');
};

const renderTechnicalIdentifiers = (model: ProductFeedbackExportModel): string => {
  const { report } = model;
  const lines = [
    '## Technical identifiers',
    '',
    `- Report ID: ${escapeInlineMarkdown(report.id)}`,
    `- Schema version: ${report.schemaVersion}`,
    `- Status version: ${report.statusVersion}`,
    `- Created at: ${formatUtc(report.createdAt)}`,
    `- Updated at: ${formatUtc(report.updatedAt)}`,
  ];

  const identifiers = [
    ['Created by UID', report.createdBy],
    ['Updated by UID', report.updatedBy],
    ['Status changed by UID', report.statusChangedBy],
    ['Attached by UID', report.attachment?.attachedBy || null],
    ['Resolved by UID', report.resolvedBy],
  ] as const;
  identifiers.forEach(([label, value]) => {
    if (value) {
      lines.push(`- ${label}: ${escapeInlineMarkdown(value)}`);
    }
  });
  lines.push(`- Status changed at: ${formatUtc(report.statusChangedAt)}`);

  return lines.join('\n');
};

export const formatProductFeedbackMarkdown = (model: ProductFeedbackExportModel): string => {
  const { report } = model;
  const sections = [
    `# Product Feedback — ${escapeInlineMarkdown(report.id)}`,
    '',
    '## Quick triage',
    '',
    `- Type: ${CATEGORY_LABELS[report.category]}`,
    `- Status: ${STATUS_LABELS[report.status]}`,
    `- Reporter role: ${escapeInlineMarkdown(report.reporterContext.roleSnapshot)}`,
    `- Route: ${inlineCode(report.runtime.routePath)}`,
    `- Viewport: ${formatDimensions(report.runtime.viewport.width, report.runtime.viewport.height)}`,
    `- DPR: ${report.runtime.viewport.devicePixelRatio}`,
    `- App version: ${escapeInlineMarkdown(report.runtime.appVersion)}`,
    `- Diagnostics: ${diagnosticSummary(model)}`,
    `- Screenshot: ${screenshotSummary(model)}`,
    `- Reported: ${formatUtc(report.createdAt, false)}`,
    '',
    '## Problem',
    '',
    fencedText(report.description),
  ];

  const optionalSections = [
    ['Expected behavior', report.expectedBehavior],
    ['Actual behavior', report.actualBehavior],
    ['Reproduction', report.reproductionNotes],
  ] as const;
  optionalSections.forEach(([heading, value]) => {
    if (hasText(value)) {
      sections.push('', `## ${heading}`, '', fencedText(value));
    }
  });

  sections.push(
    '',
    renderRuntime(model),
    '',
    renderDiagnostics(model),
    '',
    renderAttachment(model),
    '',
    renderAccessContext(model),
    '',
    renderSanitization(model),
    '',
    renderResolution(model),
    '',
    renderTechnicalIdentifiers(model)
  );

  return `${sections.join('\n').replace(/\r/g, '').replace(/\n+$/g, '')}\n`;
};

const safeFilenamePart = (value: string): string => {
  const normalized = value
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'unknown';
};

export const createProductFeedbackMarkdownExport = (
  model: ProductFeedbackExportModel
): ProductFeedbackMarkdownExport => ({
  filename: `ratuj-sprzet-product-feedback-${safeFilenamePart(model.report.id)}.md`,
  mimeType: 'text/markdown;charset=utf-8',
  content: formatProductFeedbackMarkdown(model),
});

export const createProductFeedbackMarkdownBlob = (download: ProductFeedbackMarkdownExport): Blob =>
  new Blob([UTF8_BOM, download.content], { type: download.mimeType });
